/*
Main Product
*/

class MainProduct extends HTMLElement {
  connectedCallback() {
    this.product = window.productData;
    if (!this.product) return;

    this.selectedOptions = {};
    this.selectedSellingPlan = "";

    this.variantIdInput = this.querySelector(".variant-id-input");
    // Find the selling plan input within the cart-add form
    const cartAddForm = this.querySelector("cart-add form");
    this.sellingPlanInput = cartAddForm
      ? cartAddForm.querySelector('[name="selling_plan"]')
      : this.querySelector('[name="selling_plan"]');
    this.currentPriceElement = this.querySelector(".current-price");
    this.comparePriceElement = this.querySelector(".compare-price");
    this.variantButtons = this.querySelectorAll(".variant-btn");
    this.dropdownToggles = this.querySelectorAll(".variant-dropdown-toggle");
    this.sellingPlanRadios = this.querySelectorAll(".selling-plan-radio");
    this.carousel = this.querySelector("site-carousel");

    const initialVariant =
      this.product.variants?.find((variant) => variant.available) ||
      this.product.variants?.[0];
    if (initialVariant) {
      this.selectedOptions["1"] = initialVariant.option1;
      this.selectedOptions["2"] = initialVariant.option2;
      this.selectedOptions["3"] = initialVariant.option3;
    }

    this.initializeVariantButtons();
    this.initializeDropdowns();
    this.initializeSellingPlans();

    if (initialVariant) {
      this.updateProduct(initialVariant);
    }
  }

  initializeVariantButtons() {
    this.variantButtons.forEach((btn) => {
      const position = btn.dataset.optionPosition;
      const value = btn.dataset.value;

      // Set initial active state based on first variant
      if (String(this.selectedOptions[position]) === String(value)) {
        btn.classList.add("active");
      } else {
        btn.classList.remove("active");
        btn.classList.remove("border-opacity-100");
      }

      btn.addEventListener("click", () => {
        this.handleVariantChange(position, value);
      });
    });
  }

  initializeDropdowns() {
    this.dropdownToggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        this.toggleDropdown(toggle);
      });
    });

    const dropdownOptions = this.querySelectorAll(".variant-dropdown-option");
    dropdownOptions.forEach((option) => {
      option.addEventListener("click", () => {
        const position = option.dataset.optionPosition;
        const value = option.dataset.value;
        this.handleVariantChange(position, value);
        this.closeAllDropdowns();
      });
    });

    document.addEventListener("click", (e) => {
      if (
        !e.target.closest(".variant-dropdown") &&
        !e.target.closest(".variant-dropdown-toggle")
      ) {
        this.closeAllDropdowns();
      }
    });
  }

  initializeSellingPlans() {
    this.sellingPlanRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        this.selectedSellingPlan = radio.value;
        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = radio.value;
        }
      });
    });

    const checkedSellingPlan = Array.from(this.sellingPlanRadios).find(
      (radio) => radio.checked,
    );
    const initialSellingPlan =
      checkedSellingPlan?.value || this.sellingPlanInput?.value || "";

    this.selectedSellingPlan = initialSellingPlan;
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = initialSellingPlan;
    }
  }

  handleVariantChange(position, value) {
    const colorPosition =
      this.querySelector(".variant-color")?.dataset.optionPosition;
    const previousColor = this.selectedOptions[colorPosition];

    this.selectedOptions[position] = value;

    // Not every colour comes in every size, so a pick with no exact match
    // moves the other options to the first variant that has it. Otherwise the
    // form would keep the previous variant and add the wrong colour to the cart.
    const selectedVariant =
      this.findSelectedVariant() || this.findVariantWithOption(position, value);

    if (selectedVariant) {
      this.selectedOptions["1"] = selectedVariant.option1;
      this.selectedOptions["2"] = selectedVariant.option2;
      this.selectedOptions["3"] = selectedVariant.option3;
    }

    this.updateActiveButtons();

    this.updateDropdownSelections();

    this.updateSelectedColorDisplay();

    this.updateSelectedVariantDisplay();

    if (selectedVariant) {
      this.updateProduct(selectedVariant);

      const selectedColor = this.selectedOptions[colorPosition];
      if (
        colorPosition &&
        (position === colorPosition || selectedColor !== previousColor)
      ) {
        this.updateCarouselForColorVariant(selectedColor);
      }
    }
  }

  findSelectedVariant() {
    return this.product.variants.find((variant) => {
      return (
        variant.option1 === this.selectedOptions["1"] &&
        (variant.option2 === this.selectedOptions["2"] ||
          !this.selectedOptions["2"]) &&
        (variant.option3 === this.selectedOptions["3"] ||
          !this.selectedOptions["3"])
      );
    });
  }

  findVariantWithOption(position, value) {
    const variants = this.product.variants.filter(
      (variant) => variant[`option${position}`] === value,
    );
    return variants.find((variant) => variant.available) || variants[0];
  }

  updateActiveButtons() {
    this.variantButtons.forEach((btn) => {
      const isActive =
        String(this.selectedOptions[btn.dataset.optionPosition]) ===
        String(btn.dataset.value);

      btn.classList.toggle("active", isActive);

      if (btn.classList.contains("variant-color")) {
        btn.classList.toggle("border-opacity-100", isActive);
      }
    });
  }

  updateProduct(variant) {
    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }

    this.updatePriceDisplay(variant);

    this.updateSelectedColorDisplay();

    this.updateSelectedVariantDisplay();

    // Dispatch variant changed event
    this.dispatchEvent(
      new CustomEvent("variant-changed", {
        detail: { variant },
      }),
    );
  }

  updatePriceDisplay(variant) {
    if (this.currentPriceElement) {
      this.currentPriceElement.textContent = this.formatMoney(variant.price);
    }

    if (this.comparePriceElement) {
      if (variant.compare_at_price > variant.price) {
        this.comparePriceElement.textContent = this.formatMoney(
          variant.compare_at_price,
        );
        this.comparePriceElement.parentElement.classList.remove("hidden");
      } else {
        this.comparePriceElement.parentElement.classList.add("hidden");
      }
    }
  }

  updateCarouselForColorVariant(selectedColor) {
    if (!this.carousel || !selectedColor) {
      return;
    }

    const swiper = this.carousel.swiper;
    if (!swiper) {
      return;
    }

    const colorImageIndex = this.findImageIndexForColor(selectedColor);
    if (colorImageIndex !== -1) {
      swiper.slideTo(colorImageIndex);
    }
  }

  findImageIndexForColor(colorName) {
    if (!colorName || !this.carousel) {
      return -1;
    }

    // Strategy 1: Look for slides with data-color attribute matching the selected color
    const swiperSlides = this.carousel.querySelectorAll(".swiper-slide");

    for (let i = 0; i < swiperSlides.length; i++) {
      const slide = swiperSlides[i];
      const slideColor = slide.getAttribute("data-color");

      if (
        slideColor &&
        slideColor.trim() &&
        slideColor.toLowerCase() === colorName.toLowerCase()
      ) {
        return i;
      }
    }

    // Strategy 2: Look for thumbnail buttons with data-color attribute
    const thumbnailButtons = this.carousel.querySelectorAll(".thumbnail-btn");

    for (let i = 0; i < thumbnailButtons.length; i++) {
      const button = thumbnailButtons[i];
      const buttonColor = button.getAttribute("data-color");
      const slideIndex = button.getAttribute("data-slide-index");

      if (
        buttonColor &&
        buttonColor.trim() &&
        buttonColor.toLowerCase() === colorName.toLowerCase() &&
        slideIndex !== null
      ) {
        return parseInt(slideIndex);
      }
    }

    return -1;
  }

  normalizeColorName(colorName) {
    if (!colorName) return "";

    // Common color name variations and normalizations
    const colorMap = {
      red: ["crimson", "scarlet", "burgundy", "maroon"],
      blue: ["navy", "royal", "sky", "azure"],
      green: ["emerald", "forest", "lime", "mint"],
      black: ["ebony", "charcoal", "jet"],
      white: ["ivory", "cream", "pearl"],
      gray: ["grey", "silver", "charcoal"],
      brown: ["tan", "beige", "chocolate", "coffee"],
      purple: ["violet", "lavender", "plum"],
      pink: ["rose", "magenta", "coral"],
      yellow: ["gold", "amber", "lemon"],
      orange: ["peach", "coral", "tangerine"],
    };

    const lowerColor = colorName.toLowerCase().trim();

    // Check if the color name matches any of the variations
    for (const [baseColor, variations] of Object.entries(colorMap)) {
      if (lowerColor === baseColor || variations.includes(lowerColor)) {
        return baseColor;
      }
    }

    return lowerColor;
  }

  getAvailableColors() {
    if (!this.product.options_with_values) {
      return [];
    }

    const colorOption = this.product.options_with_values.find((option) => {
      const optionNameLower = option.name.toLowerCase();
      return optionNameLower === "color" || optionNameLower === "colour";
    });

    if (colorOption) {
      return colorOption.values.map((color) => color.toLowerCase());
    }

    return [];
  }

  extractColorsFromVariants() {
    const colors = new Set();

    // Try to find colors from all variants
    if (this.product && this.product.variants) {
      this.product.variants.forEach((variant) => {
        if (!variant) return;

        // Check each option to see if it looks like a color
        [variant.option1, variant.option2, variant.option3].forEach(
          (option) => {
            if (option && option.trim() && this.looksLikeColor(option)) {
              colors.add(option.toLowerCase().trim());
            }
          },
        );
      });
    }

    return Array.from(colors);
  }

  looksLikeColor(optionValue) {
    if (!optionValue) return false;

    const colorKeywords = [
      "black",
      "white",
      "red",
      "blue",
      "green",
      "yellow",
      "orange",
      "purple",
      "pink",
      "brown",
      "gray",
      "grey",
      "navy",
      "light blue",
      "dark blue",
      "light green",
      "dark green",
      "maroon",
      "beige",
      "tan",
      "cream",
      "ivory",
      "charcoal",
      "silver",
      "gold",
      "bronze",
      "copper",
      "coral",
      "mint",
      "lime",
      "olive",
      "burgundy",
      "wine",
      "plum",
      "lavender",
      "rose",
      "peach",
      "apricot",
    ];

    const lowerValue = optionValue.toLowerCase();
    return colorKeywords.some((color) => lowerValue.includes(color));
  }

  updateDropdownSelections() {
    this.dropdownToggles.forEach((toggle) => {
      const position = toggle.dataset.optionPosition;
      const selectedText = toggle.querySelector(".selected-option-text");
      const selectedValue = this.selectedOptions[position];

      if (selectedText && selectedValue) {
        selectedText.textContent = selectedValue;
      }

      // Update dropdown option active states
      const dropdown = toggle.nextElementSibling;
      if (dropdown) {
        const options = dropdown.querySelectorAll(".variant-dropdown-option");
        options.forEach((option) => {
          option.classList.remove("active");
          if (option.dataset.value === selectedValue) {
            option.classList.add("active");
          }
        });
      }
    });
  }

  updateSelectedColorDisplay() {
    const selectedColorText = this.querySelector(".selected-color-text");
    if (selectedColorText && this.selectedOptions["1"]) {
      selectedColorText.textContent = this.selectedOptions["1"].toUpperCase();
    }
  }

  updateSelectedVariantDisplay() {
    // Update the selected variant display
    const selectedVariantDisplay = this.querySelector(
      ".selected-variant-display",
    );
    if (!selectedVariantDisplay) return;

    // Update color display
    const selectedVariantColor = selectedVariantDisplay.querySelector(
      ".selected-variant-color",
    );
    if (selectedVariantColor && this.selectedOptions["1"]) {
      selectedVariantColor.textContent =
        this.selectedOptions["1"].toUpperCase();

      // Update color swatch by finding the active color button and copying its color
      const activeColorButton = this.querySelector(".variant-color.active");
      if (activeColorButton) {
        const colorSwatch = selectedVariantDisplay.querySelector(
          ".w-4.h-4.rounded-full",
        );
        const activeColorSpan = activeColorButton.querySelector("span");
        if (colorSwatch && activeColorSpan) {
          colorSwatch.style.backgroundColor =
            activeColorSpan.style.backgroundColor;
        }
      }
    }

    // Update other option displays
    Object.keys(this.selectedOptions).forEach((position) => {
      const optionValue = this.selectedOptions[position];
      if (optionValue) {
        // Find the corresponding display element
        const displayElement = selectedVariantDisplay.querySelector(
          `.selected-variant-option-${position}`,
        );
        if (displayElement) {
          displayElement.textContent = optionValue;
        }
      }
    });
  }

  toggleDropdown(toggle) {
    const dropdown = toggle.nextElementSibling;
    const isOpen = !dropdown.classList.contains("hidden");

    // Close all dropdowns first
    this.closeAllDropdowns();

    // Open clicked dropdown if it was closed
    if (!isOpen) {
      dropdown.classList.remove("hidden");
      toggle.querySelector("svg").style.transform = "rotate(180deg)";
    }
  }

  closeAllDropdowns() {
    this.dropdownToggles.forEach((toggle) => {
      const dropdown = toggle.nextElementSibling;
      dropdown.classList.add("hidden");
      toggle.querySelector("svg").style.transform = "rotate(0deg)";
    });
  }

  getCurrentVariant() {
    return this.findSelectedVariant() || this.product.variants[0];
  }

  formatMoney(cents) {
    const locale =
      window.themeLocalization?.locale || document.documentElement.lang || "en";
    const currency = window.themeLocalization?.currency || "USD";

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (error) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(cents / 100);
    }
  }
}

customElements.define("main-product", MainProduct);
